import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import Footer from '../../components/Footer';
import { FiCalendar, FiChevronRight, FiClock, FiShare2, FiBookmark } from 'react-icons/fi';
import { API_BASE_URL, BASE_URL,institute_id } from '../../config/config';
import Seo from '../../components/Seo';
import { toast } from 'react-toastify';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readingTime, setReadingTime] = useState(0);


  // Calculate reading time
  const calculateReadingTime = (text) => {
    const wordsPerMinute = 200;
    const words = text.replace(/<[^>]*>/g, '').split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  // Extract excerpt from description
  const getExcerpt = (html, length = 160) => {
    const text = html?.replace(/<[^>]*>/g, '') || '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  useEffect(() => {
    fetchBlogPost();
    fetchRelatedPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (post) {
      const time = calculateReadingTime((post.description || '') + (post.short_description || ''));
      setReadingTime(time);
    }
  }, [post]);
  

  const fetchBlogPost = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pages/blog?institute_id=${institute_id}`);
      const data = await response.json();
      
      if (data.success) {
        const foundPost = data.data.find(p => p.url === slug);
        if (foundPost) {
          setPost(foundPost);
        }
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pages/blog?institute_id=${institute_id}`);
      const data = await response.json();
      
      if (data.success) {
        setRelatedPosts(data.data.filter(p => p.url !== slug).slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching related posts:', error);
    }
  };

  // Share functionality
  const shareOnWhatsApp = () => {
    const text = `${post.title}\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`);
  };

  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`);
  };

  const shareOnTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="bg-gray-200 h-96 rounded-lg mb-8"></div>
            <div className="bg-gray-200 h-8 rounded w-3/4 mb-4"></div>
            <div className="bg-gray-200 h-4 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Blog Post Not Found</h2>
          <Link to="/blog" className="btn-primary inline-block">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const postUrl = `blog/${post.url}`;
  const postImage = post.image ? `${post.image}` : `default-blog-image.jpg`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced SEO */}
      <Seo 
        title={`${post.title} | Brainershub Blog`}
        description={getExcerpt(post.short_description || post.description, 160)}
        keywords={`${post.title}, competitive exams, UPSC, SSC, banking, brainershub`}
        image={postImage}
        url={postUrl}
        type="article"
        publishedTime={post.cdate}
        modifiedTime={post.udate || post.cdate}
        category="Education"
        tags={['competitive exams', 'education', 'online learning']}
      />
      
      <PublicHeader />

      <article className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Breadcrumb - Enhanced with structured data */}
          <nav className="flex items-center gap-2 text-sm text-gray-600 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600">Home</Link>
            <FiChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-primary-600">Blog</Link>
            <FiChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">{post.title}</span>
          </nav>

          {/* Post Header */}
          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>
            
            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-gray-600 mb-6">
              <span className="flex items-center gap-2">
                <FiCalendar className="w-5 h-5" />
                {new Date(post.cdate).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
              <span className="flex items-center gap-2">
                <FiClock className="w-5 h-5" />
                {readingTime} min read
              </span>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-3 pb-6 border-b">
              <span className="text-sm font-medium text-gray-700">Share:</span>
              <button
                onClick={shareOnWhatsApp}
                className="p-2 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors"
                aria-label="Share on WhatsApp"
                title="Share on WhatsApp"
              >
                <FiShare2 className="w-5 h-5" />
              </button>
              <button
                onClick={shareOnFacebook}
                className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                aria-label="Share on Facebook"
                title="Share on Facebook"
              >
                <FiShare2 className="w-5 h-5" />
              </button>
              <button
                onClick={shareOnTwitter}
                className="p-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 transition-colors"
                aria-label="Share on Twitter"
                title="Share on Twitter"
              >
                <FiShare2 className="w-5 h-5" />
              </button>
              <button
                onClick={copyLink}
                className="p-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                aria-label="Copy link"
                title="Copy link"
              >
                <FiBookmark className="w-5 h-5" />
              </button>
            </div>
          </header>

          {/* Featured Image */}
          {post.image && (
            <figure className="mb-8 w-90 h-70 flex items-center justify-center p-4 mb-4  rounded-xl overflow-hidden shadow-xl">
              <img
                src={postImage}
                alt={post.title}
                className="w-full h-full object-contain"
                loading="lazy"
                width="1200"
                height="630"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1200" height="600"%3E%3Crect fill="%236366f1" width="1200" height="600"/%3E%3C/svg%3E';
                }}
              />
            </figure>
          )}

          {/* Post Content */}
          <div className="prose prose-lg max-w-none mb-8">
            {post.short_description && (
              <div 
                dangerouslySetInnerHTML={{ __html: post.short_description }}
                className="text-gray-700 leading-relaxed mb-6 text-xl font-medium"
              />
            )}
            
            <div 
              dangerouslySetInnerHTML={{ __html: post.description }}
              className="text-gray-700 leading-relaxed"
            />
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-primary-500 to-purple-600 rounded-xl p-8 text-white text-center my-12">
            <h3 className="text-2xl font-bold mb-4">Ready to Start Your Preparation?</h3>
            <p className="text-lg mb-6 opacity-90">
              Explore our comprehensive courses and ace your competitive exams
            </p>
            <Link 
              to="/courses" 
              className="inline-block px-8 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Browse Courses
            </Link>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              Related Articles
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.url}`}
                  className="card hover:shadow-xl transition-all group"
                >
                  {relatedPost.image && (
                    <div className="relative mb-4 rounded-lg overflow-hidden">
                      <img
                        src={`${relatedPost.image}`}
                        alt={relatedPost.title}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%236366f1" width="400" height="300"/%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                  )}
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {relatedPost.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {getExcerpt(relatedPost.short_description, 100)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPost;

